import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types/user.types';

/**
 * Feature-flag gate for the Investment Proposal Builder.
 *
 * Lives at Firestore `system/featureFlags.investmentProposals`:
 *
 *   {
 *     betaUserIds: string[]   // explicit user-doc IDs allowed during beta
 *     releasedToAll: boolean  // when true, fall back to the role-based
 *                                permission (anyone in the role list sees it)
 *   }
 *
 * Access rules (in evaluation order):
 *   1. Super users always see it (no override possible).
 *   2. If the user's doc ID is in `betaUserIds`, they see it.
 *   3. If `releasedToAll === true`, anyone in the role allowlist sees it.
 *   4. Otherwise, no.
 *
 * Operational rollout:
 *   - Beta phase: `betaUserIds: ['<pieter-doc-id>']`, `releasedToAll: false`
 *                 → super users + Pieter only
 *   - After validation: flip `releasedToAll: true`
 *                 → all roles with the dashboardCards.investmentProposals
 *                   permission see it (Lvl 1, Lvl 2, HQ Mgmt, super)
 *
 * Realtime via onSnapshot — flag changes propagate within ~1s, no app
 * reload needed.
 */

const ROLES_WITH_PROPOSAL_ACCESS_WHEN_RELEASED: UserRole[] = [
  UserRole.PROPCLOUDAI_SUPER_USER,
  UserRole.HQ_MANAGER,
  UserRole.HQ_ADMIN,
  UserRole.HQ_CONSULTANT_LVL1,
  UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
];

interface InvestmentProposalsFlag {
  betaUserIds?: string[];
  releasedToAll?: boolean;
}

export function useCanAccessInvestmentProposals(): { canAccess: boolean; loading: boolean } {
  const { userProfile } = useAuth();
  const [flag, setFlag] = useState<InvestmentProposalsFlag | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'system', 'featureFlags'),
      (snap) => {
        const data = snap.exists() ? (snap.data() as Record<string, unknown>) : {};
        const ip = (data.investmentProposals as InvestmentProposalsFlag | undefined) ?? {};
        setFlag(ip);
        setLoading(false);
      },
      (err) => {
        console.warn('[useCanAccessInvestmentProposals] flag load failed', err);
        setFlag({});
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  if (!userProfile) return { canAccess: false, loading };

  // Super users always have access — administrative override.
  if (userProfile.role === UserRole.PROPCLOUDAI_SUPER_USER) {
    return { canAccess: true, loading };
  }

  // Per-user beta allowlist (regardless of role gating).
  const betaIds = flag?.betaUserIds ?? [];
  if (betaIds.includes(userProfile.id)) {
    return { canAccess: true, loading };
  }

  // Public release — fall back to role permission.
  if (flag?.releasedToAll && ROLES_WITH_PROPOSAL_ACCESS_WHEN_RELEASED.includes(userProfile.role)) {
    return { canAccess: true, loading };
  }

  return { canAccess: false, loading };
}
